import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { FormActionButtons } from '@/components/layout/form-action-buttons';
import { FormHeaderWithStatus } from '@/components/layout/form-header-with-status';
import { Card } from '@/components/ui/card';
import {
        Form,
        FormControl,
        FormField,
        FormItem,
        FormLabel,
        FormMessage
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { useEntityForm } from '@/hooks/useEntityForm';
import { useUnsavedChanges } from '@/hooks/useUnsavedChanges';

const optionalNumber = (field: string) =>
        z
                .union([
                        z.number({ invalid_type_error: `${field} must be a number` }),
                        z.undefined()
                ])
                .refine(value => value === undefined || Number.isFinite(value), {
                        message: `${field} must be a valid number`
                })
                .refine(value => value === undefined || value > 0, {
                        message: `${field} must be greater than 0`
                })
                .optional();

const preprocessNumber = (field: string) =>
        z.preprocess(value => {
                if (value === '' || value === null || value === undefined) {
                        return undefined;
                }

                if (typeof value === 'string') {
                        const parsed = Number(value);
                        return Number.isNaN(parsed) ? value : parsed;
                }

                return value;
        }, optionalNumber(field));

const createLimitFormSchema = (t: (key: string) => string) => z
        .object({
                name: z
                        .string({ required_error: t('form.nameRequired') })
                        .trim()
                        .min(1, t('form.nameRequired')),
                minAmount: preprocessNumber('Minimum amount'),
                maxAmount: preprocessNumber('Maximum amount'),
                minStayMinutes: preprocessNumber('Minimum stay minutes'),
                maxStayMinutes: preprocessNumber('Maximum stay minutes')
        })
		.superRefine((values, ctx) => {
			if (
                        values.minAmount !== undefined &&
                        values.maxAmount !== undefined &&
                        values.minAmount > values.maxAmount
                ) {
                        ctx.addIssue({
                                code: z.ZodIssueCode.custom,
                                path: ['maxAmount'],
                                message: t('form.minGreaterThanMax')
                        });
                }

                if (
                        values.minStayMinutes !== undefined &&
                        values.maxStayMinutes !== undefined &&
                        values.minStayMinutes > values.maxStayMinutes
                ) {
                        ctx.addIssue({
                                code: z.ZodIssueCode.custom,
                                path: ['maxStayMinutes'],
                                message: t('form.minStayGreaterThanMax')
                        });
                }
        });

export type LimitFormValues = z.infer<ReturnType<typeof createLimitFormSchema>>;

export interface LimitFormSubmitData extends LimitFormValues {
        limitId?: string;
}

interface LimitFormProps {
        mode: 'create' | 'edit' | 'clone';
        initialData?: LimitFormSubmitData;
        onSubmit: (data: LimitFormSubmitData) => void;
	isSubmitting: boolean;
	onResetForm?: (resetFn: () => void) => void;
	headerTitle: string;
	onBack?: () => void;
}

const buildInitialValues = (
        data?: LimitFormSubmitData
): LimitFormValues | undefined => {
        if (!data) {
                return undefined;
        }

        return {
                name: data.name ?? '',
                minAmount: data.minAmount ?? undefined,
                maxAmount: data.maxAmount ?? undefined,
                minStayMinutes: data.minStayMinutes ?? undefined,
                maxStayMinutes: data.maxStayMinutes ?? undefined
        };
};

export const LimitForm = ({
        mode,
        initialData,
        onSubmit,
        isSubmitting,
        onResetForm,
        headerTitle,
        onBack
}: LimitFormProps) => {
        const { t } = useTranslation('limits');
        const nameInputRef = useRef<HTMLInputElement>(null);
        const memoizedInitialData = useMemo(() => buildInitialValues(initialData), [initialData]);

        // Create schema with translations
        const limitFormSchema = useMemo(() => createLimitFormSchema(t), [t]);

        const form = useEntityForm<LimitFormValues>({
                mode,
                schema: limitFormSchema,
                defaultValues: {
                        name: '',
                        minAmount: undefined,
                        maxAmount: undefined,
                        minStayMinutes: undefined,
                        maxStayMinutes: undefined
                },
                initialData: memoizedInitialData
        });

        useUnsavedChanges(form.formState.isDirty, {
                isSubmitSuccessful: form.formState.isSubmitSuccessful,
                isSubmitting
        });

        const { control, handleSubmit, reset } = form;

        // Auto-focus name field when creating a new limit
        useEffect(() => {
                if (mode === 'create' || mode === 'clone') {
                        setTimeout(() => {
                                nameInputRef.current?.focus();
                        }, 0);
                }
        }, [mode]);

        useEffect(() => {
                if (!onResetForm) {
                        return;
                }

                onResetForm(() => {
                        reset(memoizedInitialData);
                });
        }, [onResetForm, reset, memoizedInitialData]);

        const handleFormSubmit = (values: LimitFormValues) => {
                onSubmit({
                        ...values,
                        limitId: initialData?.limitId
                });
        };

        // Handle button click when using type="button" to avoid triggering parent form submission
        const handleButtonSubmit = () => {
                handleSubmit(handleFormSubmit)();
        };

        // For create and clone modes, use button type to avoid parent form issues
        const isCreateOrClone = mode === 'create' || mode === 'clone';

        return (
                <>
                        <FormHeaderWithStatus
                                title={headerTitle}
                                onBack={onBack}
                                rightContent={
                                        <FormActionButtons
                                                mode={mode}
                                                onSubmit={isCreateOrClone ? handleButtonSubmit : undefined}
                                                isSubmitting={isSubmitting}
                                                submitType={isCreateOrClone ? 'button' : 'submit'}
                                                validationErrors={form.formState.errors}
                                                formId="limit-form"
                                        />
                                }
                        />
                        <Form {...form}>
                        <form id="limit-form" onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
                                <Card className="space-y-4">
                                        <div>
                                                <h2 className="text-lg font-semibold">{t('form.limitDefinition')}</h2>
                                                <p className="text-sm text-muted-foreground">
                                                        {t('form.limitDefinitionDesc')}
                                                </p>
                                        </div>
                                        <div className="space-y-6">
                                                <FormField
                                                        control={control}
                                                        name="name"
                                                        render={({ field }) => (
                                                                <FormItem>
                                                                        <FormLabel>{t('form.name')} *</FormLabel>
                                                                        <FormControl>
                                                                                <Input
                                                                                        {...field}
                                                                                        ref={nameInputRef}
                                                                                        maxLength={255}
                                                                                        placeholder={t('form.namePlaceholder')}
                                                                                />
                                                                        </FormControl>
                                                                        <FormMessage />
                                                                </FormItem>
                                                        )}
                                                />
                                                <div className="grid gap-4 md:grid-cols-2">
                                                        <FormField
                                                                control={control}
                                                                name="minAmount"
                                                                render={({ field }) => (
                                                                        <FormItem>
                                                                                <FormLabel>{t('form.minimumAmount')}</FormLabel>
                                                                                <FormControl>
                                                                                        <Input
                                                                                                type="number"
                                                                                                step="0.01"
                                                                                                min="0.01"
                                                                                                value={field.value ?? ''}
                                                                                                onChange={event => {
                                                                                                        const value = event.target.value;
                                                                                                        field.onChange(
                                                                                                                value === ''
                                                                                                                        ? undefined
                                                                                                                        : Number(value)
                                                                                                        );
                                                                                                }}
                                                                                                placeholder="0.01"
                                                                                        />
                                                                                </FormControl>
                                                                                <FormMessage />
                                                                        </FormItem>
                                                                )}
                                                        />
                                                        <FormField
                                                                control={control}
                                                                name="maxAmount"
                                                                render={({ field }) => (
                                                                        <FormItem>
                                                                                <FormLabel>{t('form.maximumAmount')}</FormLabel>
                                                                                <FormControl>
                                                                                        <Input
                                                                                                type="number"
                                                                                                step="0.01"
                                                                                                min="0.01"
                                                                                                value={field.value ?? ''}
                                                                                                onChange={event => {
                                                                                                        const value = event.target.value;
                                                                                                        field.onChange(
                                                                                                                value === ''
                                                                                                                        ? undefined
                                                                                                                        : Number(value)
                                                                                                        );
                                                                                                }}
                                                                                                placeholder="0.01"
                                                                                        />
                                                                                </FormControl>
                                                                                <FormMessage />
                                                                        </FormItem>
                                                                )}
                                                        />
                                                </div>
                                                <div className="grid gap-4 md:grid-cols-2">
                                                        <FormField
                                                                control={control}
                                                                name="minStayMinutes"
                                                                render={({ field }) => (
                                                                        <FormItem>
                                                                                <FormLabel>{t('form.minimumStay')}</FormLabel>
                                                                                <FormControl>
                                                                                        <Input
                                                                                                type="number"
                                                                                                min={1}
                                                                                                value={field.value ?? ''}
                                                                                                onChange={event => {
                                                                                                        const value = event.target.value;
                                                                                                        field.onChange(
                                                                                                                value === ''
                                                                                                                        ? undefined
                                                                                                                        : Number(value)
                                                                                                        );
                                                                                                }}
                                                                                                placeholder="1"
                                                                                        />
                                                                                </FormControl>
                                                                                <FormMessage />
                                                                        </FormItem>
                                                                )}
                                                        />
                                                        <FormField
                                                                control={control}
                                                                name="maxStayMinutes"
                                                                render={({ field }) => (
                                                                        <FormItem>
                                                                                <FormLabel>{t('form.maximumStay')}</FormLabel>
                                                                                <FormControl>
                                                                                        <Input
                                                                                                type="number"
                                                                                                min={1}
                                                                                                value={field.value ?? ''}
                                                                                                onChange={event => {
                                                                                                        const value = event.target.value;
                                                                                                        field.onChange(
                                                                                                                value === ''
                                                                                                                        ? undefined
                                                                                                                        : Number(value)
                                                                                                        );
                                                                                                }}
                                                                                                placeholder="1"
                                                                                        />
                                                                                </FormControl>
                                                                                <FormMessage />
                                                                        </FormItem>
                                                                )}
                                                        />
                                                </div>
                                        </div>
                                </Card>
                        </form>
                </Form>
                </>
        );
};
